/**
 * SettingsPage — User configuration and account management
 *
 * Sections:
 *   - Account info + sign out
 *   - Install App (PWA install prompt)
 *   - Notifications (FCM enable/disable)
 *   - Payday configuration (schedule, anchor date, conservative income)
 *   - Checking balance override
 *   - Data management (seed bills, reset cycles)
 *
 * Phase 7: Install App button, notification toggle
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserConfig, saveUserConfig, resetBillCycles, getIncomeSources, addIncomeSource, updateIncomeSource, deleteIncomeSource, getIncomeSuggestions, acceptIncomeSuggestion, dismissIncomeSuggestion, getTellerEnrollments, getAccounts, toggleAccountSync, saveUserCategory, deleteUserCategory, initUserCategoryDefs, deleteAllUserCategoryDefs, getUserCategoryDefs, saveUserBucketNames } from '../services/db';
import { useCategoryContext, DEFAULT_BUCKET_NAMES } from '../contexts/CategoryContext';
import { CATEGORY_TAXONOMY } from '../utils/categories';
import { callDeleteUserData, callDisconnectTellerEnrollment, callTriggerSync } from '../services/functions';
import TellerConnect from '../components/TellerConnect';
import { requestNotificationPermission, disableNotifications } from '../services/notifications';
import theme from '../utils/theme';

export default function SettingsPage({ installPrompt, onInstalled }) {
  const { user, logout } = useAuth();
  const { categoryDefs, bucketNames, usingDefaults, refresh: refreshCategories, setBucketNames } = useCategoryContext();
  const [config, setConfig] = useState(null); // eslint-disable-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [tellerEnrollments, setTellerEnrollments] = useState([]);
  const [tellerAccounts, setTellerAccounts] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);
  const [alertPrefs, setAlertPrefs] = useState({
    budget_exceeded: true,
    pace_warning: true,
    overspend_projection: true,
    category_spike: true,
    savings_rate: true,
    money_on_fire: true,
    pace_threshold: 80,
  });

  // Form state
  const [paydaySchedule, setPaydaySchedule] = useState('biweekly');
  const [paydayAnchor, setPaydayAnchor] = useState('');

  // Income sources state
  const [incomeSources, setIncomeSources] = useState([]);
  const [incomeSuggestions, setIncomeSuggestions] = useState([]);
  const [showIncomeForm, setShowIncomeForm] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const BLANK_INCOME_FORM = {
    name: '',
    match_pattern: '',
    expected_amount: '',
    frequency: 'biweekly',
    anchor_date: '',
    variable: false,
    is_primary: false,
  };
  const [incomeForm, setIncomeForm] = useState(BLANK_INCOME_FORM);

  // ---- Categories & Buckets state ----
  const [catEditId, setCatEditId] = useState(null);   // id (or label for defaults) being renamed
  const [catEditLabel, setCatEditLabel] = useState('');
  const [addingBucket, setAddingBucket] = useState(null); // bucket key we're adding to
  const [addCatLabel, setAddCatLabel] = useState('');
  const [bucketNameDraft, setBucketNameDraft] = useState({ ...DEFAULT_BUCKET_NAMES });
  const [catSaving, setCatSaving] = useState(false);

  // Keep draft in sync when context loads
  useEffect(() => { setBucketNameDraft({ ...bucketNames }); }, [bucketNames]);

  /**
   * Initialize user_category_defs from CATEGORY_TAXONOMY if empty, then return fresh rows.
   * Call before any write operation so all rows have DB-assigned ids.
   */
  const ensureAndGetDefs = async () => {
    if (!usingDefaults) return categoryDefs;
    const allDefs = CATEGORY_TAXONOMY.flatMap(g => g.categories);
    await initUserCategoryDefs(user.id, allDefs);
    const fresh = await getUserCategoryDefs(user.id);
    return fresh;
  };

  const handleBucketNameBlur = async (bucket) => {
    const newName = (bucketNameDraft[bucket] || '').trim();
    if (!newName || newName === bucketNames[bucket]) return;
    const updated = { ...bucketNames, [bucket]: newName };
    setBucketNames(updated);
    await saveUserBucketNames(user.id, updated);
  };

  const handleStartCatEdit = (label) => {
    setCatEditId(label);
    setCatEditLabel(label);
  };

  const handleSaveCatRename = async (originalLabel, bucket) => {
    const newLabel = catEditLabel.trim();
    setCatEditId(null);
    if (!newLabel || newLabel === originalLabel) return;
    setCatSaving(true);
    try {
      const defs = await ensureAndGetDefs();
      const row = defs.find(d => d.label === originalLabel);
      if (row) {
        await saveUserCategory(user.id, { id: row.id, label: newLabel, bucket, sort_order: row.sort_order });
      } else {
        await saveUserCategory(user.id, { label: newLabel, bucket });
      }
      await refreshCategories();
    } catch (err) {
      showMessage('Failed to rename: ' + err.message, 'error');
    } finally {
      setCatSaving(false);
    }
  };

  const handleMoveCat = async (label, bucket, newBucket) => {
    setCatSaving(true);
    try {
      const defs = await ensureAndGetDefs();
      const row = defs.find(d => d.label === label);
      if (row) {
        await saveUserCategory(user.id, { id: row.id, label, bucket: newBucket, sort_order: row.sort_order });
      } else {
        await saveUserCategory(user.id, { label, bucket: newBucket });
      }
      await refreshCategories();
    } catch (err) {
      showMessage('Failed to move: ' + err.message, 'error');
    } finally {
      setCatSaving(false);
    }
  };

  const handleDeleteCat = async (label) => {
    if (!window.confirm(`Delete "${label}"?`)) return;
    setCatSaving(true);
    try {
      const defs = await ensureAndGetDefs();
      const row = defs.find(d => d.label === label);
      if (row) await deleteUserCategory(user.id, row.id);
      await refreshCategories();
    } catch (err) {
      showMessage('Failed to delete: ' + err.message, 'error');
    } finally {
      setCatSaving(false);
    }
  };

  const handleAddCat = async (bucket) => {
    const label = addCatLabel.trim();
    if (!label) return;
    setCatSaving(true);
    try {
      const defs = await ensureAndGetDefs();
      if (defs.find(d => d.label === label)) {
        showMessage('Category already exists', 'error');
        return;
      }
      await saveUserCategory(user.id, { label, bucket, sort_order: defs.length });
      setAddingBucket(null);
      setAddCatLabel('');
      await refreshCategories();
    } catch (err) {
      showMessage('Failed to add: ' + err.message, 'error');
    } finally {
      setCatSaving(false);
    }
  };

  const handleResetCategories = async () => {
    if (!window.confirm('Reset to default categories? All customizations will be lost.')) return;
    setCatSaving(true);
    try {
      await deleteAllUserCategoryDefs(user.id);
      await refreshCategories();
      showMessage('Reset to defaults.', 'success');
    } catch (err) {
      showMessage('Failed to reset: ' + err.message, 'error');
    } finally {
      setCatSaving(false);
    }
  };

  // Build per-bucket category list for the settings UI (works for defaults and custom defs)
  const bucketKeys = ['income', 'need', 'want', 'savings'];
  const getCatsForBucket = (bucket) => {
    if (usingDefaults) {
      return CATEGORY_TAXONOMY.flatMap(g => g.categories.filter(c => c.bucket === bucket));
    }
    return categoryDefs.filter(d => d.bucket === bucket);
  };

  /**
   * Load user config from Firestore on mount.
   */
  const loadConfig = useCallback(async () => {
    try {
      const [data, sources, suggestions, enrollments, accounts] = await Promise.all([
        getUserConfig(user.id),
        getIncomeSources(user.id),
        getIncomeSuggestions(user.id),
        getTellerEnrollments(user.id),
        getAccounts(user.id),
      ]);
      setTellerEnrollments(enrollments);
      setTellerAccounts(accounts);
      if (data) {
        setConfig(data);
        setPaydaySchedule(data.payday_schedule || 'biweekly');
        setPaydayAnchor(data.payday_anchor || '');
        setNotificationsEnabled(data.notifications_enabled || false);
        if (data.alert_preferences) {
          setAlertPrefs(prev => ({ ...prev, ...data.alert_preferences }));
        }
      }
      setIncomeSources(sources);
      setIncomeSuggestions(suggestions);
    } catch (err) {
      console.error('Failed to load config:', err);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  /**
   * Save payday and income configuration.
   */
  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await saveUserConfig(user.id, {
        payday_schedule: paydaySchedule,
        payday_anchor: paydayAnchor,
        primary_checking_mask: '5581',
      });
      showMessage('Settings saved!', 'success');
      await loadConfig();
    } catch (err) {
      showMessage('Failed to save: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle PWA install prompt.
   */
  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      onInstalled?.();
      showMessage('App installed!', 'success');
    }
  };

  /**
   * Toggle push notifications on/off.
   */
  const handleToggleNotifications = async () => {
    setNotificationsLoading(true);
    try {
      if (notificationsEnabled) {
        await disableNotifications(user.id);
        setNotificationsEnabled(false);
        showMessage('Notifications disabled', 'info');
      } else {
        const token = await requestNotificationPermission(user.id);
        if (token) {
          setNotificationsEnabled(true);
          showMessage('Notifications enabled!', 'success');
        } else {
          showMessage('Permission denied or unsupported', 'warning');
        }
      }
    } catch (err) {
      showMessage('Failed: ' + err.message, 'error');
    } finally {
      setNotificationsLoading(false);
    }
  };

  /**
   * Update a single alert preference and save to Firestore.
   */
  const handleAlertPrefChange = async (key, value) => {
    const updated = { ...alertPrefs, [key]: value };
    setAlertPrefs(updated);
    try {
      await saveUserConfig(user.id, { alert_preferences: updated });
    } catch (err) {
      console.error('Failed to save alert prefs:', err);
    }
  };

  /**
   * Reset all bills to unpaid for a new billing cycle.
   */
  const handleResetCycles = async () => {
    if (!window.confirm('Reset all bills to "unpaid" for this month? This is usually done on the 1st.')) return;
    setSaving(true);
    try {
      const count = await resetBillCycles(user.id);
      showMessage(`Reset ${count} bills to unpaid.`, 'success');
    } catch (err) {
      showMessage('Failed to reset: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  /** Accept a detected income suggestion — creates an income source. */
  const handleAcceptIncomeSuggestion = async (sug) => {
    try {
      await acceptIncomeSuggestion(user.id, sug.id);
      setIncomeSuggestions(prev => prev.filter(s => s.id !== sug.id));
      const updated = await getIncomeSources(user.id);
      setIncomeSources(updated);
      showMessage(`Added "${sug.description}" as an income source!`, 'success');
    } catch (err) {
      showMessage('Failed to accept: ' + err.message, 'error');
    }
  };

  /** Accept all pending income suggestions at once. */
  const handleAcceptAllIncomeSuggestions = async () => {
    if (incomeSuggestions.length === 0) return;
    try {
      await Promise.all(incomeSuggestions.map(s => acceptIncomeSuggestion(user.id, s.id)));
      setIncomeSuggestions([]);
      const updated = await getIncomeSources(user.id);
      setIncomeSources(updated);
      showMessage(`Added ${incomeSuggestions.length} income source${incomeSuggestions.length > 1 ? 's' : ''}!`, 'success');
    } catch (err) {
      showMessage('Failed to accept all: ' + err.message, 'error');
    }
  };

  /** Dismiss a detected income suggestion. */
  const handleDismissIncomeSuggestion = async (sugId) => {
    try {
      await dismissIncomeSuggestion(user.id, sugId);
      setIncomeSuggestions(prev => prev.filter(s => s.id !== sugId));
    } catch (err) {
      showMessage('Failed to dismiss: ' + err.message, 'error');
    }
  };

  /** Open the income source form for adding or editing. */
  const handleOpenIncomeForm = (source = null) => {
    if (source) {
      setEditingSource(source);
      setIncomeForm({
        name: source.name || '',
        match_pattern: source.match_pattern || '',
        expected_amount: source.expected_amount ?? '',
        frequency: source.frequency || 'biweekly',
        anchor_date: source.anchor_date || paydayAnchor,
        variable: source.variable || false,
        is_primary: source.is_primary || false,
      });
    } else {
      setEditingSource(null);
      // Check if we already have a primary source
      const hasPrimary = incomeSources.some(s => s.is_primary);
      setIncomeForm({
        ...BLANK_INCOME_FORM,
        anchor_date: paydayAnchor,
        frequency: paydaySchedule,
        is_primary: !hasPrimary, // auto-mark first source as primary
      });
    }
    setShowIncomeForm(true);
  };

  /** Save (add or update) an income source. */
  const handleSaveIncomeSource = async () => {
    if (!incomeForm.name) {
      showMessage('Name is required.', 'error');
      return;
    }
    setSaving(true);
    try {
      // Auto-generate match pattern from name (first 2 words, used to find deposits in transactions)
      const autoPattern = incomeForm.name.trim().split(/\s+/).slice(0, 2).join(' ');
      const payload = {
        name: incomeForm.name,
        match_pattern: incomeForm.match_pattern || autoPattern,
        expected_amount: incomeForm.variable ? 0 : Number(incomeForm.expected_amount || 0),
        frequency: incomeForm.frequency,
        anchor_date: incomeForm.is_primary ? paydayAnchor : incomeForm.anchor_date,
        variable: incomeForm.variable || false,
        is_primary: incomeForm.is_primary || false,
      };

      // If marking as primary, unmark any other primary source first
      if (payload.is_primary) {
        const currentPrimary = incomeSources.find(s => s.is_primary && s.id !== editingSource?.id);
        if (currentPrimary) {
          await updateIncomeSource(user.id, currentPrimary.id, { is_primary: false });
        }
        // Also sync payday config anchor/schedule from this primary source
        await saveUserConfig(user.id, {
          payday_schedule: payload.frequency,
          payday_anchor: payload.anchor_date,
        });
      }

      if (editingSource) {
        await updateIncomeSource(user.id, editingSource.id, payload);
        showMessage('Income source updated!', 'success');
      } else {
        await addIncomeSource(user.id, payload);
        showMessage('Income source added!', 'success');
      }
      setShowIncomeForm(false);
      setEditingSource(null);
      const updated = await getIncomeSources(user.id);
      setIncomeSources(updated);
    } catch (err) {
      showMessage('Failed to save: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  /** Delete an income source. */
  const handleDeleteIncomeSource = async (sourceId, name) => {
    if (!window.confirm(`Remove "${name}" as an income source?`)) return;
    try {
      await deleteIncomeSource(user.id, sourceId);
      setIncomeSources(prev => prev.filter(s => s.id !== sourceId));
      showMessage('Income source removed.', 'info');
    } catch (err) {
      showMessage('Failed to delete: ' + err.message, 'error');
    }
  };

  /** Reload Teller enrollments and accounts. */
  const loadTellerEnrollments = async () => {
    try {
      const [enrollments, accounts] = await Promise.all([
        getTellerEnrollments(user.id),
        getAccounts(user.id),
      ]);
      setTellerEnrollments(enrollments);
      setTellerAccounts(accounts);
    } catch (err) {
      console.error('Failed to load Teller enrollments:', err);
    }
  };

  /** Toggle whether an account's transactions are included in syncs. */
  const handleToggleAccountSync = async (accountId, current) => {
    const next = !current;
    setTellerAccounts(prev => prev.map(a => a.id === accountId ? { ...a, include_in_sync: next } : a));
    try {
      await toggleAccountSync(accountId, next);
    } catch (err) {
      setTellerAccounts(prev => prev.map(a => a.id === accountId ? { ...a, include_in_sync: current } : a));
      showMessage('Failed to update account: ' + err.message, 'error');
    }
  };

  /** Trigger a manual Teller sync. */
  const handleSync = async () => {
    setSyncLoading(true);
    setMessage(null);
    try {
      const result = await callTriggerSync();
      await loadTellerEnrollments();
      setMessage({ type: 'success', text: `Sync complete — ${result.accounts} accounts, ${result.transactions} transactions updated.` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Sync failed: ' + err.message });
    } finally {
      setSyncLoading(false);
    }
  };

  /** Disconnect a Teller enrollment and refresh the list. */
  const handleDisconnectTeller = async (enrollmentId, institutionName) => {
    if (!window.confirm(`Disconnect ${institutionName}? This will stop syncing transactions from this bank.`)) return;
    try {
      await callDisconnectTellerEnrollment(enrollmentId);
      await loadTellerEnrollments();
      showMessage(`${institutionName} disconnected.`, 'info');
    } catch (err) {
      showMessage('Failed to disconnect: ' + err.message, 'error');
    }
  };

  /**
   * Permanently delete all user data and sign out (GDPR erasure).
   * Requires typing DELETE to confirm.
   */
  const handleDeleteAccount = async () => {
    if (deleteInput !== 'DELETE') return;
    setDeleting(true);
    try {
      await callDeleteUserData();
      await logout();
    } catch (err) {
      showMessage('Delete failed: ' + err.message, 'error');
      setDeleting(false);
    }
  };

  /** Flash a temporary status message. */
  const showMessage = (text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  // Detect iOS for unsupported notification message
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  if (loading) return <div className="card"><p className="empty-state">Loading settings...</p></div>;

  return (
    <div>
      {/* Status message */}
      {message && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Account */}
      <div className="card">
        <h2>Account</h2>
        <div className="settings-section">
          <p style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
            <strong>{user?.displayName}</strong>
          </p>
          <p style={{ fontSize: '0.8rem', color: theme.textSecondary, marginBottom: '0.75rem' }}>
            {user?.email}
          </p>
          <button className="btn btn-secondary btn-sm" onClick={logout}>Sign Out</button>
        </div>
      </div>

      {/* Install App — only shown when install prompt is available */}
      {installPrompt && (
        <div className="card">
          <h2>Install App</h2>
          <div className="settings-section">
            <p style={{ fontSize: '0.8rem', color: theme.textSecondary, marginBottom: '0.5rem' }}>
              Add Spendable to your home screen for a native app experience.
            </p>
            <button className="btn btn-success" onClick={handleInstall}>
              Install App
            </button>
          </div>
        </div>
      )}

      {/* Notifications */}
      <div className="card">
        <h2>Notifications</h2>
        <div className="settings-section">
          {isIOS ? (
            <p style={{ fontSize: '0.8rem', color: theme.textMuted }}>
              Push notifications are not supported on iOS Safari.
              Install the app via "Add to Home Screen" first, then enable notifications.
            </p>
          ) : (
            <>
              <p style={{ fontSize: '0.8rem', color: theme.textSecondary, marginBottom: '0.5rem' }}>
                Get alerts for sync completion, upcoming bills, and budget warnings.
              </p>
              <button
                className={`btn ${notificationsEnabled ? 'btn-secondary' : 'btn-primary'} btn-sm`}
                onClick={handleToggleNotifications}
                disabled={notificationsLoading}
              >
                {notificationsLoading
                  ? 'Working...'
                  : notificationsEnabled
                    ? 'Disable Notifications'
                    : 'Enable Notifications'}
              </button>

              {/* Alert type preferences — only shown when notifications enabled */}
              {notificationsEnabled && (
                <div style={{ marginTop: '1rem' }}>
                  <h3 style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Alert Types</h3>
                  {[
                    { key: 'budget_exceeded', label: 'Budget exceeded' },
                    { key: 'pace_warning', label: 'Pace warning' },
                    { key: 'overspend_projection', label: 'Overspend projection' },
                    { key: 'category_spike', label: 'Category spikes' },
                    { key: 'savings_rate', label: 'Low savings rate' },
                    { key: 'money_on_fire', label: 'Avoidable fees' },
                  ].map(({ key, label }) => (
                    <div key={key} className="alert-pref-row">
                      <input
                        type="checkbox"
                        id={`alert-${key}`}
                        checked={alertPrefs[key] !== false}
                        onChange={e => handleAlertPrefChange(key, e.target.checked)}
                      />
                      <label htmlFor={`alert-${key}`}>{label}</label>
                    </div>
                  ))}

                  <div style={{ marginTop: '0.75rem' }}>
                    <label style={{ fontSize: '0.85rem', color: theme.textDark }}>
                      Pace warning threshold: <strong>{alertPrefs.pace_threshold}%</strong>
                    </label>
                    <input
                      type="range"
                      min="50" max="95" step="5"
                      value={alertPrefs.pace_threshold}
                      onChange={e => handleAlertPrefChange('pace_threshold', Number(e.target.value))}
                      style={{ width: '100%', marginTop: '0.25rem' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: theme.textMuted }}>
                      <span>50%</span>
                      <span>95%</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Connected Banks (Teller) */}
      <div className="card">
        <h2>Connected Banks</h2>
        <div className="settings-section">
          <p style={{ fontSize: '0.8rem', color: theme.textSecondary, marginBottom: '0.75rem' }}>
            Connect your bank accounts for real-time balance and transaction sync.
            Your credentials are handled directly by Teller — we never see them.
          </p>

          {tellerEnrollments.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              {tellerEnrollments.map(enrollment => {
                const enrollmentAccounts = tellerAccounts.filter(a => a.enrollment_id === enrollment.id);
                return (
                  <div key={enrollment.id} style={{ paddingBottom: '0.75rem', marginBottom: '0.75rem', borderBottom: `1px solid ${theme.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{enrollment.institution_name}</div>
                        <div style={{ fontSize: '0.72rem', color: enrollment.status === 'active' ? theme.bucketSavings : theme.danger }}>
                          {enrollment.status === 'active'
                            ? enrollment.last_synced
                              ? `Synced ${new Date(enrollment.last_synced?.toDate?.() || enrollment.last_synced).toLocaleString()}`
                              : 'Connected — first sync in progress'
                            : 'Disconnected — reconnect below'}
                        </div>
                      </div>
                      {enrollment.status === 'active' && (
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => handleDisconnectTeller(enrollment.id, enrollment.institution_name)}>
                          Disconnect
                        </button>
                      )}
                    </div>
                    {enrollmentAccounts.length > 0 && (
                      <div style={{ paddingLeft: '0.5rem' }}>
                        {enrollmentAccounts.map(acct => (
                          <div key={acct.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.25rem 0' }}>
                            <div style={{ fontSize: '0.8rem', color: theme.textDark }}>
                              {acct.name} {acct.mask ? `••${acct.mask}` : ''}
                              <span style={{ fontSize: '0.7rem', color: theme.textMuted, marginLeft: '0.4rem' }}>
                                {acct.subtype || acct.type}
                              </span>
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', color: theme.textSecondary, cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={acct.include_in_sync !== false}
                                onChange={() => handleToggleAccountSync(acct.id, acct.include_in_sync !== false)}
                              />
                              Sync
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <TellerConnect onEnrollmentAdded={async () => { await loadTellerEnrollments(); handleSync(); }} />
            {tellerEnrollments.some(e => e.status === 'active') && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleSync}
                disabled={syncLoading}
              >
                {syncLoading ? 'Syncing...' : 'Sync Now'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Income — payday config + detected sources + manual sources, all in one card */}
      <div className="card">
        <h2>Income</h2>

        {/* Conservative monthly floor summary */}
        {incomeSources.length > 0 && (() => {
          const monthlyFloor = incomeSources.reduce((sum, src) => {
            if (src.variable) return sum;
            const perPaycheck = src.expected_amount || 0;
            const multiplier = src.frequency === 'biweekly' ? 2.167
              : src.frequency === 'weekly' ? 4.333
              : src.frequency === 'semimonthly' ? 2
              : 1;
            return sum + perPaycheck * multiplier;
          }, 0);
          return (
            <div style={{ background: theme.tintSavings, border: `1px solid ${theme.bucketSavings}22`, borderRadius: '8px', padding: '0.5rem 0.75rem', marginBottom: '1rem', fontSize: '0.8rem' }}>
              <strong>Conservative monthly floor: ${Math.round(monthlyFloor).toLocaleString()}/mo</strong>
              <span style={{ color: theme.textMuted, marginLeft: '0.5rem' }}>(sum of fixed source floors)</span>
            </div>
          );
        })()}

        {/* Income sources list */}
        {incomeSources.length === 0 && !showIncomeForm && (
          <p style={{ fontSize: '0.8rem', color: theme.textMuted, marginBottom: '0.75rem' }}>
            No income sources yet. Add your first one below.
          </p>
        )}

        {incomeSources.map(src => (
          <div key={src.id} className="income-source-row">
            <div className="income-source-info">
              <div className="income-source-name">
                {src.name}
                {src.is_primary && <span style={{ fontSize: '0.65rem', background: theme.bucketNeed, color: 'white', borderRadius: '4px', padding: '0.1rem 0.35rem', marginLeft: '0.4rem' }}>PRIMARY</span>}
                {src.variable && <span style={{ fontSize: '0.65rem', background: theme.bucketWant, color: 'white', borderRadius: '4px', padding: '0.1rem 0.35rem', marginLeft: '0.4rem' }}>VARIABLE</span>}
              </div>
              <div className="income-source-meta">
                {src.variable
                  ? 'Variable — counts as extra, not baseline'
                  : `$${Number(src.expected_amount).toLocaleString()} min · ${src.frequency}`
                }
              </div>
            </div>
            <div className="income-source-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => handleOpenIncomeForm(src)}>Edit</button>
              <button className="btn btn-danger btn-sm" onClick={() => handleDeleteIncomeSource(src.id, src.name)}>×</button>
            </div>
          </div>
        ))}

        {/* Add / edit income source form */}
        {showIncomeForm ? (
          <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: `1px solid ${theme.border}` }}>
            <div className="form-group">
              <label>Employer / Source Name</label>
              <input type="text" placeholder="e.g. Employer, Side Business, Benefits"
                value={incomeForm.name} onChange={e => setIncomeForm(p => ({ ...p, name: e.target.value }))} />
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" id="income-variable"
                checked={incomeForm.variable}
                onChange={e => setIncomeForm(p => ({ ...p, variable: e.target.checked, expected_amount: e.target.checked ? 0 : p.expected_amount }))} />
              <label htmlFor="income-variable" style={{ marginBottom: 0, fontWeight: 500 }}>Amount changes each payment (bonus, freelance, etc.)</label>
            </div>
            {incomeForm.variable ? (
              <p style={{ fontSize: '0.7rem', color: theme.textMuted, marginBottom: '0.5rem' }}>
                Every deposit from this source will count as extra — none of it is factored into your baseline budget.
              </p>
            ) : (
              <div className="form-group">
                <label>Worst-case payment amount ($)</label>
                <input type="number" placeholder="e.g. 2500" min="0" step="1"
                  value={incomeForm.expected_amount} onChange={e => setIncomeForm(p => ({ ...p, expected_amount: e.target.value }))} />
                <p style={{ fontSize: '0.7rem', color: theme.textMuted, marginTop: '0.2rem' }}>
                  The least you'd ever expect. Anything above this is a bonus.
                </p>
              </div>
            )}

            <div className="form-group">
              <label>How often do you get paid?</label>
              <select value={incomeForm.frequency} onChange={e => { setIncomeForm(p => ({ ...p, frequency: e.target.value })); if (incomeForm.is_primary) setPaydaySchedule(e.target.value); }}>
                <option value="biweekly">Every 2 weeks</option>
                <option value="weekly">Weekly</option>
                <option value="semimonthly">Twice a month (1st & 15th)</option>
                <option value="monthly">Once a month (fixed date)</option>
                <option value="monthly-last-business-day">Once a month (last business day)</option>
              </select>
            </div>

            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" id="income-primary"
                checked={incomeForm.is_primary}
                onChange={e => { setIncomeForm(p => ({ ...p, is_primary: e.target.checked })); if (e.target.checked) setPaydaySchedule(incomeForm.frequency); }} />
              <label htmlFor="income-primary" style={{ marginBottom: 0, fontWeight: 500 }}>This is my main paycheck</label>
            </div>

            {/* Anchor date — shown for primary (drives spendable clock) or non-last-business-day secondary */}
            {(incomeForm.is_primary || (!incomeForm.is_primary && incomeForm.frequency !== 'monthly-last-business-day')) && (
              <div style={incomeForm.is_primary ? { background: theme.tintNeed, border: `1px solid ${theme.bucketNeed}22`, borderRadius: '8px', padding: '0.75rem', marginBottom: '0.5rem' } : {}}>
                {incomeForm.is_primary && (
                  <p style={{ fontSize: '0.75rem', color: theme.textSecondary, marginBottom: '0.5rem' }}>
                    Used to calculate your spendable balance and days until next payday.
                  </p>
                )}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Most recent payday</label>
                  <input type="date" value={incomeForm.is_primary ? paydayAnchor : incomeForm.anchor_date}
                    onChange={e => {
                      if (incomeForm.is_primary) { setPaydayAnchor(e.target.value); setIncomeForm(p => ({ ...p, anchor_date: e.target.value })); }
                      else { setIncomeForm(p => ({ ...p, anchor_date: e.target.value })); }
                    }} />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={handleSaveIncomeSource} disabled={saving}>
                {saving ? 'Saving...' : editingSource ? 'Update' : 'Add Source'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowIncomeForm(false); setEditingSource(null); }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button className="btn btn-success btn-sm" style={{ marginTop: incomeSources.length > 0 ? '0.25rem' : 0 }} onClick={() => handleOpenIncomeForm()}>
            + Add Income Source
          </button>
        )}

        {/* Detected income suggestions — shown inline below sources */}
        {incomeSuggestions.length > 0 && (
          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${theme.border}` }}>
            <div className="sub-suggestions-header" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ color: theme.bucketSavings, fontSize: '0.9rem' }}>Detected Income Sources</h3>
              <button className="btn btn-success btn-sm" onClick={handleAcceptAllIncomeSuggestions}>
                Accept All ({incomeSuggestions.length})
              </button>
            </div>
            {incomeSuggestions.map(sug => (
              <div key={sug.id} className="sub-sug-row" style={{ background: theme.tintSavings }}>
                <div className="sub-sug-info">
                  <div className="sub-sug-name">{sug.description}</div>
                  <div className="sub-sug-meta">
                    {sug.variable
                      ? `Variable · ~$${Math.round(sug.avg_amount).toLocaleString()} avg · ${sug.occurrence_count} occurrences`
                      : `~$${Math.round(sug.avg_amount).toLocaleString()}/${sug.frequency === 'biweekly' ? 'paycheck' : sug.frequency} · ${sug.occurrence_count} occurrences`
                    }
                    {sug.variable && <span style={{ marginLeft: '0.35rem', fontSize: '0.65rem', background: theme.bucketWant, color: 'white', borderRadius: '4px', padding: '0.1rem 0.3rem' }}>VARIABLE</span>}
                    {' · '}seen in {sug.months_seen?.length || 0} months
                  </div>
                </div>
                <div className="sub-sug-actions">
                  <button className="btn btn-success btn-sm" onClick={() => handleAcceptIncomeSuggestion(sug)}>+ Add</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleDismissIncomeSuggestion(sug.id)}>&times;</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Payday config fallback — only shown when no income sources defined yet */}
        {incomeSources.length === 0 && !showIncomeForm && (
          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${theme.border}` }}>
            <h3 style={{ fontSize: '0.85rem', marginBottom: '0.5rem', color: theme.textDark }}>Payday Setup</h3>
            <p style={{ fontSize: '0.8rem', color: theme.textSecondary, marginBottom: '0.75rem' }}>
              Add an income source above and check "This is my main paycheck" to set this automatically.
            </p>
            <div className="form-group">
              <label>How often do you get paid?</label>
              <select value={paydaySchedule} onChange={e => setPaydaySchedule(e.target.value)}>
                <option value="biweekly">Biweekly (every 2 weeks)</option>
                <option value="weekly">Weekly</option>
                <option value="semimonthly">Semi-monthly (1st & 15th)</option>
                <option value="monthly">Monthly (fixed date)</option>
                <option value="monthly-last-business-day">Monthly (last business day)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Most recent payday</label>
              <input type="date" value={paydayAnchor} onChange={e => setPaydayAnchor(e.target.value)} />
            </div>
            <button className="btn btn-primary btn-sm" onClick={handleSaveConfig} disabled={saving}>
              {saving ? 'Saving...' : 'Save Payday Settings'}
            </button>
          </div>
        )}
      </div>

      {/* Data Management */}
      <div className="card">
        <h2>Data Management</h2>

        <div className="settings-section">
          <h3>Reset Bill Cycles</h3>
          <p style={{ fontSize: '0.8rem', color: theme.textSecondary, marginBottom: '0.5rem' }}>
            Mark all bills as "unpaid" for the new month. Usually done automatically
            on the 1st.
          </p>
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleResetCycles}
            disabled={saving}
          >
            Reset All to Unpaid
          </button>
        </div>
      </div>

      {/* Categories & Buckets */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h2 style={{ marginBottom: 0 }}>Categories & Buckets</h2>
          {!usingDefaults && (
            <button className="btn btn-secondary btn-sm" onClick={handleResetCategories} disabled={catSaving}>
              Reset
            </button>
          )}
        </div>

        {usingDefaults && (
          <p style={{ fontSize: '0.8rem', color: theme.textSecondary, marginBottom: '0.75rem' }}>
            Using defaults. Rename, move, add, or delete any category below — changes save instantly.
          </p>
        )}

        {bucketKeys.map(bucket => {
          const cats = getCatsForBucket(bucket);
          const bucketColor = { need: theme.bucketNeed, want: theme.bucketWant, savings: theme.bucketSavings, income: theme.bucketIncome };

          return (
            <div key={bucket} className="cat-bucket-section">
              {/* Bucket name — editable inline */}
              <div className="cat-bucket-header" style={{ borderColor: bucketColor[bucket] + '44' }}>
                <input
                  className="cat-bucket-name-input"
                  style={{ color: bucketColor[bucket] }}
                  value={bucketNameDraft[bucket] || ''}
                  onChange={e => setBucketNameDraft(p => ({ ...p, [bucket]: e.target.value }))}
                  onBlur={() => handleBucketNameBlur(bucket)}
                  onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                  title="Tap to rename this bucket"
                />
                <span style={{ fontSize: '0.65rem', color: theme.textMuted, marginLeft: '0.25rem' }}>✎</span>
              </div>

              {/* Category rows */}
              {cats.map(cat => (
                <div key={cat.label} className="cat-def-row">
                  {catEditId === cat.label ? (
                    <input
                      className="cat-def-name-input"
                      value={catEditLabel}
                      autoFocus
                      onChange={e => setCatEditLabel(e.target.value)}
                      onBlur={() => handleSaveCatRename(cat.label, cat.bucket)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') e.target.blur();
                        if (e.key === 'Escape') setCatEditId(null);
                      }}
                    />
                  ) : (
                    <span className="cat-def-name" onClick={() => handleStartCatEdit(cat.label)}>
                      {cat.label}
                    </span>
                  )}

                  <select
                    className="cat-def-bucket-select"
                    value={cat.bucket}
                    onChange={e => handleMoveCat(cat.label, cat.bucket, e.target.value)}
                    disabled={catSaving}
                  >
                    {bucketKeys.map(b => (
                      <option key={b} value={b}>{bucketNameDraft[b] || DEFAULT_BUCKET_NAMES[b]}</option>
                    ))}
                  </select>

                  <button
                    className="cat-def-delete"
                    onClick={() => handleDeleteCat(cat.label)}
                    disabled={catSaving}
                    title="Delete category"
                  >
                    ×
                  </button>
                </div>
              ))}

              {/* Add category */}
              {addingBucket === bucket ? (
                <div className="cat-add-row">
                  <input
                    autoFocus
                    className="cat-add-input"
                    value={addCatLabel}
                    placeholder="Category name..."
                    onChange={e => setAddCatLabel(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleAddCat(bucket);
                      if (e.key === 'Escape') { setAddingBucket(null); setAddCatLabel(''); }
                    }}
                  />
                  <button className="btn btn-primary btn-sm" onClick={() => handleAddCat(bucket)} disabled={catSaving || !addCatLabel.trim()}>
                    Add
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setAddingBucket(null); setAddCatLabel(''); }}>
                    ×
                  </button>
                </div>
              ) : (
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: '0.35rem', fontSize: '0.78rem' }}
                  onClick={() => { setAddingBucket(bucket); setAddCatLabel(''); }}
                >
                  + Add
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Danger Zone */}
      <div className="card" style={{ borderColor: theme.danger, borderWidth: '1px', borderStyle: 'solid' }}>
        <h2 style={{ color: theme.danger }}>Danger Zone</h2>
        <div className="settings-section">
          <h3>Delete Account</h3>
          <p style={{ fontSize: '0.8rem', color: theme.textSecondary, marginBottom: '0.75rem' }}>
            Permanently deletes all your data — bills, transactions, goals, settings, and history.
            This cannot be undone.
          </p>
          {!deleteConfirm ? (
            <button className="btn btn-danger btn-sm" onClick={() => setDeleteConfirm(true)}>
              Delete My Account
            </button>
          ) : (
            <div>
              <p style={{ fontSize: '0.85rem', color: theme.danger, marginBottom: '0.5rem', fontWeight: 600 }}>
                Type DELETE to confirm permanent deletion:
              </p>
              <input
                type="text"
                value={deleteInput}
                onChange={e => setDeleteInput(e.target.value)}
                placeholder="DELETE"
                style={{ marginBottom: '0.5rem', borderColor: theme.danger }}
              />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handleDeleteAccount}
                  disabled={deleteInput !== 'DELETE' || deleting}
                >
                  {deleting ? 'Deleting...' : 'Permanently Delete Everything'}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => { setDeleteConfirm(false); setDeleteInput(''); }}
                  disabled={deleting}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
